import {
    Box,
    Divider,
    Flex,
    Heading,
    Image,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { SafeHTML } from "components/safe-html";
import ActivityBadges from "components/selection-components/select-activity/activity-badges/activity-badges";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { urlBuilder } from "search-params/url-builder";
import { AvailabilityState } from "utils/availability-utils";
import { AvailableTimesSection } from "types/available-times";
import { ImageServiceAspectRatio } from "types/image-service-aspect-ratio";
import { ClickIdBuilders } from "logger/click-tracking";

interface IActivityItem {
    section: AvailableTimesSection;
    onClick: () => void;
    availabilityState?: AvailabilityState | null;
}

const ActivityItem = ({ section, onClick, availabilityState }: IActivityItem) => {
    const { t } = useTranslation();
    const [showImage, setShowImage] = useState(true);

    const imageParams: Record<string, string | undefined> = {
        AspectRatio: ImageServiceAspectRatio.OneByOne,
        Type: "webp",
    };
    const baseUrl = `${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${section.image}`;
    const sectionImageUrlOneByOne = urlBuilder(baseUrl, imageParams);

    return (
        <Box
            as="button"
            w="full"
            onClick={onClick}
            type="button"
            aria-label={t("viewDetailsOfXActivity", { activity: section.name })}
            data-track-click={ClickIdBuilders.selectActivity(section.name)}
            // @ts-expect-error // ! TODO: Include _focuse for box in casper-ui-library
            _focus={{ boxShadow: "var(--theme-shadows-outline)" }}
            _hover={{ bg: ThemeColorVariable.AccentHoverAlpha }}
        >
            <Flex
                justify="space-between"
                py={ThemeSpaceVariable.Medium}
                align="center"
                gap={ThemeSpaceVariable.Small}
                flex={1}
                px={{ md: ThemeSpaceVariable.Small }}
            >
                <Stack spacing={ThemeSpaceVariable.None}>
                    <Heading
                        as="h3"
                        noOfLines={1}
                        fontFamily={ThemeFontVariable.SubHeader}
                        fontWeight="medium"
                        color={ThemeColorVariable.OnBackgroundHeader}
                        fontSize={{
                            base: ThemeFontSizeVariable.Large,
                            md: ThemeFontSizeVariable.XLarge,
                        }}
                    >
                        {section.name}
                    </Heading>
                    <Text
                        height={{
                            base: ThemeFontSizeVariable.XLarge,
                            md: "unset",
                        }}
                        noOfLines={1}
                        whiteSpace="break-spaces"
                        fontSize={{
                            base: ThemeFontSizeVariable.Small,
                            md: ThemeFontSizeVariable.Large,
                        }}
                        maxW="380px"
                    >
                        <SafeHTML
                            element="span"
                            options={{ allowedTags: ["br"], allowedSchemes: [] }}
                            html={section.description}
                        />
                    </Text>
                    <ActivityBadges sections={[section]} availabilityState={availabilityState} />
                </Stack>
                {section.image && showImage && (
                    <Image
                        src={sectionImageUrlOneByOne}
                        maxH="72px"
                        borderRadius={ThemeSpaceVariable.Small}
                        onError={() => setShowImage(false)}
                    />
                )}
            </Flex>
            <Divider />
        </Box>
    );
};

export default ActivityItem;
